import { useEffect, useRef, useState } from "react";
import css from "./index.module.css";
import Content from "@components/adminNavigation";
import axios from "axios";
import Popup from "@components/popup";
import * as Types from "../../../types";
import useUserData from "@hooks/useUserData";
import SEO from "@components/seo";
import ChatMessage from "@components/chatMessage";

interface Chat {
  user: Types.User;
  msgs: Types.Message[];
}

export default function Chats() {
  const [selectedChat, setSelectedChat] = useState<Chat | null>();
  const [users, setUsers] = useState<Types.User[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<NodeJS.Timeout | null>(null);
  const [msg, setMsg] = useState("");
  const { userData } = useUserData();
  const [isUserPopupOpen, setIsUserPopupOpen] = useState(false);
  const [userInfo, setUserInfo] = useState<Types.User | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      selectedChat &&
      messagesContainerRef.current &&
      messagesEndRef.current
    ) {
      const timeout = setTimeout(() => {
        try {
          messagesEndRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "end",
          });
        } catch {}
      }, 0);
      return () => clearTimeout(timeout);
    }
  }, [selectedChat?.user?.uuid]);

  useEffect(() => {
    if (selectedChat && messagesEndRef.current) {
      setTimeout(() => {
        try {
          messagesEndRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "end",
          });
        } catch {}
      }, 0);
    }
  }, [selectedChat?.msgs?.length]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 767);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    loadChats();

    const interval = setInterval(() => {
      loadChats();
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const loadChats = () => {
    axios
      .get(`${import.meta.env.VITE_API_ADM_URL}/chats`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      })
      .then((response) => {
        setUsers(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const selectChat = (user: Types.User) => {
    if (wsRef.current && isConnected) {
      wsRef.current.send(JSON.stringify({ type: 2, chat: user.uuid }));
    }

    axios
      .get(`${import.meta.env.VITE_API_ADM_URL}/chat/${user.uuid}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      })
      .then((response) => {
        setSelectedChat(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const openUserInfo = () => {
    if (!selectedChat) return;
    axios
      .get(
        `${import.meta.env.VITE_API_ADM_URL}/user/${selectedChat.user.uuid}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      )
      .then((response) => {
        setUserInfo(response.data);
        setIsUserPopupOpen(true);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const connect = () => {
    const ws = new WebSocket(import.meta.env.VITE_API_WEBSOCKET);

    ws.onopen = () => {
      const token = localStorage.getItem("authToken");
      ws.send(JSON.stringify({ type: 0, jwt: token, chat: "adm" }));
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.type == 0 && data.status == "Authorized") {
        setIsConnected(true);
        if (reconnectTimer.current) {
          clearTimeout(reconnectTimer.current);
        }
      } else if (data.type === 1) {
        setSelectedChat((prevChat: any) => {
          if (!prevChat) {
            return {
              user: data?.user,
              msgs: [
                {
                  msgType: data?.msgType,
                  from: data?.from,
                  to: data?.to,
                  msg: data?.msg,
                  createdAt: data?.createdAt,
                },
              ],
            };
          }

          return {
            ...prevChat,
            msgs: [
              ...prevChat.msgs,
              {
                msgType: data?.msgType,
                from: data?.from,
                to: data?.to,
                msg: data?.msg,
                createdAt: data?.createdAt,
              },
            ],
          };
        });
      }
    };

    ws.onclose = () => {
      setIsConnected(false);
      reconnect();
    };

    ws.onerror = (_) => {
      ws.close();
    };

    wsRef.current = ws;
  };

  const reconnect = () => {
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
    }
    reconnectTimer.current = setTimeout(() => {
      connect();
    }, 3000);
  };

  useEffect(() => {
    connect();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
      }
    };
  }, [import.meta.env.VITE_API_WEBSOCKET]);

  const sendMessage = () => {
    if (wsRef.current && isConnected && msg.trim()) {
      setMsg("");
      wsRef.current.send(JSON.stringify({ type: 1, msgType: 0, msg }));
      // Автоскролл после отправки
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({
            behavior: "smooth",
            block: "end",
          });
        }
      }, 50); // Чуть быстрее, чтобы сработало на лайве
    } else {
      console.warn("WebSocket is not connected. Message not sent.");
    }
  };

  const handleFileUpload = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_ADM_URL}/uploadFile`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      const imageUrl = response.data.url;
      wsRef.current!.send(
        JSON.stringify({ type: 1, msgType: 1, msg: imageUrl })
      );
    } catch (error) {
      console.log(error);
    }
  };

  const setStatus = (id: string) => {
    if (!selectedChat) return;

    axios
      .post(
        `${import.meta.env.VITE_API_ADM_URL}/chat/${selectedChat.user.uuid}`,
        { status: id },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      )
      .then((_) => {
        if (id === "0") {
          loadChats();
          setSelectedChat(null);
        } else {
          selectChat(selectedChat.user);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // Мобильная версия: два режима - список и чат
  if (isMobile) {
    return (
      <Content isBack={false}>
        <SEO
          title={"Чаты"}
          description={"Админ-панель"}
          ogUrl={`https://рыбныимир.рф/`}
        />

        {!selectedChat ? (
          // Список чатов
          <div className={css.mobileChatsList}>
            <h1 className={css.mobileChatsTitle}>Чаты</h1>
            <div className={css.mobileChatsContainer}>
              {users.map((user) => (
                <div
                  key={user.uuid}
                  className={css.mobileChatItem}
                  onClick={() => selectChat(user)}
                >
                  <img
                    src="https://amu.edu.kz/upload/default-avatar.jpg"
                    alt="avatar"
                  />
                  <div className={css.mobileChatInfo}>
                    <b className={css.mobileChatName}>
                      {user.firstName ? user.firstName : user.phone}
                    </b>
                    <a className={css.mobileChatPhone}>{user.phone}</a>
                    {user.msgStatus == "1" && (
                      <span className={css.mobileChatBadge}>
                        Ожидает ответа
                      </span>
                    )}
                  </div>
                  <i className="fi fi-rr-angle-right"></i>
                </div>
              ))}
              {users.length <= 0 && (
                <div className={css.mobileChatsEmpty}>
                  <i className="fi fi-rr-comments"></i>
                  <a>Нет доступных чатов</a>
                </div>
              )}
            </div>
          </div>
        ) : (
          // Выбранный чат: сообщения + ввод
          <div className={css.mobileChatView}>
            <div className={css.mobileChatHeader}>
              <div
                className={css.mobileChatHeaderInfo}
                onClick={openUserInfo}
                style={{ cursor: "pointer" }}
              >
                <img
                  src="https://amu.edu.kz/upload/default-avatar.jpg"
                  alt="avatar"
                />
                <div className={css.mobileChatHeaderText}>
                  <b className={css.mobileChatHeaderName}>
                    {selectedChat?.user?.firstName
                      ? selectedChat?.user?.firstName
                      : selectedChat?.user?.phone}
                  </b>
                  <b className={css.mobileChatHeaderStatus}>
                    {selectedChat?.user?.msgStatus == "1"
                      ? "Ожидает ответа"
                      : `В работе у администратора`}
                  </b>
                </div>
              </div>
              <div className={css.mobileChatHeaderActions}>
                {selectedChat.user.msgStatus === userData.id && (
                  <a
                    onClick={() => setStatus("0")}
                    className={css.mobileChatActionBtn}
                  >
                    <i className="fi fi-rr-cross-circle"></i>
                  </a>
                )}
                <a
                  href={`tel:${selectedChat?.user?.phone}`}
                  className={css.mobileChatActionBtn}
                >
                  <i className="fi fi-rr-phone-call"></i>
                </a>
              </div>
            </div>

            <div
              className={css.mobileMessagesList}
              ref={messagesContainerRef}
              style={{ overflowY: "auto" }}
            >
              {selectedChat?.msgs.map((item, index) => (
                <ChatMessage
                  key={index}
                  item={item}
                  isFromMe={item.to === "admin"}
                  renderMode="mobile"
                />
              ))}
              <div ref={messagesEndRef} />
            </div>

            {selectedChat.user.msgStatus === userData.id && (
              <div className={css.mobileInputContainer}>
                <textarea
                  className={css.mobileInput}
                  placeholder="Введите сообщение"
                  value={msg}
                  onChange={(event) => setMsg(event.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                />
                <div className={css.mobileInputActions}>
                  <label
                    htmlFor="mobileFileInput"
                    className={css.mobileFileBtn}
                  >
                    <i className="fi fi-rr-picture"></i>
                  </label>
                  <input
                    type="file"
                    id="mobileFileInput"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        handleFileUpload(e.target.files[0]);
                      }
                    }}
                    accept="image/*"
                  />
                  <button
                    className={css.mobileSendBtn}
                    onClick={sendMessage}
                    disabled={!msg.trim()}
                  >
                    <i className="fi fi-rr-paper-plane"></i>
                  </button>
                </div>
              </div>
            )}

            {selectedChat.user.msgStatus === "1" && (
              <div
                className={css.mobileTakeWorkBtn}
                onClick={() => setStatus(userData.id)}
              >
                Взять в работу
              </div>
            )}
          </div>
        )}

        <Popup showPopup={isUserPopupOpen} setShowPopup={setIsUserPopupOpen}>
          <h2>Информация о пользователе</h2>
          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ display: "grid", gap: 6 }}>
              <label>Имя</label>
              <input type="text" value={userInfo?.firstName || ""} readOnly />
            </div>
            <div style={{ display: "grid", gap: 6 }}>
              <label>Фамилия</label>
              <input type="text" value={userInfo?.lastName || ""} readOnly />
            </div>
            <div style={{ display: "grid", gap: 6 }}>
              <label>Номер телефона</label>
              <input type="text" value={userInfo?.phone || ""} readOnly />
            </div>
            <div style={{ display: "grid", gap: 6 }}>
              <label>Логин</label>
              <input type="text" value={userInfo?.username || ""} readOnly />
            </div>
            <div style={{ display: "grid", gap: 6 }}>
              <label>Уровень доступа</label>
              <input
                type="text"
                value={userInfo?.access?.toString() || ""}
                readOnly
              />
            </div>
            <div style={{ display: "grid", gap: 6 }}>
              <label>Наименование организации</label>
              <input
                type="text"
                value={userInfo?.information?.name || ""}
                readOnly
              />
            </div>
            <div style={{ display: "grid", gap: 6 }}>
              <label>ИНН</label>
              <input
                type="text"
                value={userInfo?.information?.inn || ""}
                readOnly
              />
            </div>
            <div style={{ display: "grid", gap: 6 }}>
              <label>Комментарий администратора</label>
              <textarea
                value={userInfo?.adminComment || ""}
                readOnly
                style={{ height: 100 }}
              />
            </div>
          </div>
        </Popup>
      </Content>
    );
  }

  // Десктопная версия: прокачанный вывод bubble-сообщений
  return (
    <Content isBack={false}>
      <SEO
        title={"Чаты"}
        description={"Админ-панель"}
        ogUrl={`https://рыбныимир.рф/`}
      />

      <div className={css.content}>
        <div className={css.chats}>
          {users.map((user) => (
            <div
              key={user.uuid}
              className={css.chat}
              style={{ justifyContent: "space-between" }}
              onClick={() => selectChat(user)}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <img
                  src="https://amu.edu.kz/upload/default-avatar.jpg"
                  alt="avatar"
                />
                <div className={css.info}>
                  <b className={css.name}>
                    {user.firstName ? user.firstName : user.phone}
                  </b>
                  <a className={css.name}>{user.information.name}</a>
                  <a className={css.name}>{user.phone}</a>
                </div>
              </div>

              {user.msgStatus == "1" ? (
                <span
                  className={css.mobileChatBadge}
                  style={{ background: "red", padding: 9 }}
                >
                  <i
                    style={{ position: "relative", top: 1 }}
                    className="fi fi-rr-stopwatch"
                  ></i>
                </span>
              ) : (
                user.msgStatus === userData.id && (
                  <span
                    className={css.mobileChatBadge}
                    style={{ background: "green", padding: 9 }}
                  >
                    <i
                      style={{ position: "relative", top: 1 }}
                      className="fi fi-rr-briefcase"
                    ></i>
                  </span>
                )
              )}
            </div>
          ))}
          {users.length <= 0 && (
            <div className={css.notSelected}>Нет доступных чатов</div>
          )}
        </div>

        {selectedChat != null ? (
          <div className={css.messages}>
            <div className={css.chat}>
              <div
                className={css.information}
                onClick={openUserInfo}
                style={{ cursor: "pointer" }}
              >
                <img
                  src="https://amu.edu.kz/upload/default-avatar.jpg"
                  alt="avatar"
                />
                <div className={css.uInfo}>
                  <b className={css.name}>
                    {selectedChat?.user?.firstName
                      ? selectedChat?.user?.firstName
                      : selectedChat?.user?.phone}
                  </b>
                  <b className={css.status}>
                    {selectedChat?.user?.msgStatus == "1"
                      ? "Ожидает ответа"
                      : `В работе у  администратора`}
                  </b>
                </div>
              </div>

              <div className={css.actions}>
                {selectedChat.user.msgStatus === userData.id && (
                  <a
                    onClick={() => setStatus("0")}
                    style={{
                      color: "red",
                      fontSize: 22,
                      position: "relative",
                      top: 4,
                      marginRight: 5,
                    }}
                  >
                    <i className="fi fi-rr-cross-circle"></i>
                  </a>
                )}
                <a href={`tel:${selectedChat?.user?.phone}`}>
                  <i className="fi fi-rr-phone-call"></i>
                </a>
              </div>
            </div>

            <div
              style={{
                height: "100%",
                overflowY: "auto",
                marginTop: 10,
                padding: "0 1.5vw 0 0",
              }}
              ref={messagesContainerRef}
            >
              <div className={css.msgList}>
                {selectedChat?.msgs.map((item, index) => (
                  <ChatMessage
                    key={index}
                    item={item}
                    isFromMe={item.to === "admin"}
                    renderMode="desktop"
                  />
                ))}
                <div ref={messagesEndRef} />
              </div>
            </div>

            {selectedChat.user.msgStatus === userData.id && (
              <>
                <div className={css.input}>
                  <textarea
                    placeholder="Введите сообщение"
                    value={msg}
                    onChange={(event) => setMsg(event.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                  />

                  <div className={css.file}>
                    <label
                      htmlFor="fileInput"
                      style={{
                        display: "block",
                        textAlign: "center",
                        backgroundColor: "#2b2a2a",
                        color: "white",
                        padding: "10px",
                        cursor: "pointer",
                        width: "100%",
                        borderRadius: 10,
                        fontSize: 15,
                        boxSizing: "border-box",
                        border: "1px solid var(--border-color)",
                      }}
                    >
                      <i className="fi fi-rr-picture" style={{ top: 2 }}></i>
                    </label>
                    <input
                      type="file"
                      id="fileInput"
                      name="avatar"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          handleFileUpload(e.target.files[0]);
                        }
                      }}
                      accept="image/*"
                    />
                  </div>

                  <div
                    className={css.send}
                    onClick={sendMessage}
                    style={{
                      cursor: !msg.trim() ? "not-allowed" : "pointer",
                      opacity: !msg.trim() ? 0.6 : 1,
                    }}
                  >
                    <i className="fi fi-rr-paper-plane"></i>
                  </div>
                </div>
              </>
            )}

            {selectedChat.user.msgStatus === "1" && (
              <div
                className={css.btnAction}
                onClick={() => setStatus(userData.id)}
              >
                Взять в работу
              </div>
            )}
          </div>
        ) : (
          <div className={css.notSelected}>
            <i className="fi fi-rr-info"></i>
            <a>Не выбран пользователь</a>
          </div>
        )}
      </div>

      <Popup showPopup={isUserPopupOpen} setShowPopup={setIsUserPopupOpen}>
        <h2>Информация о пользователе</h2>
        <div style={{ display: "grid", gap: 10 }}>
          <div style={{ display: "grid", gap: 6 }}>
            <label>Имя</label>
            <input type="text" value={userInfo?.firstName || ""} readOnly />
          </div>
          <div style={{ display: "grid", gap: 6 }}>
            <label>Фамилия</label>
            <input type="text" value={userInfo?.lastName || ""} readOnly />
          </div>
          <div style={{ display: "grid", gap: 6 }}>
            <label>Номер телефона</label>
            <input type="text" value={userInfo?.phone || ""} readOnly />
          </div>
          <div style={{ display: "grid", gap: 6 }}>
            <label>Логин</label>
            <input type="text" value={userInfo?.username || ""} readOnly />
          </div>
          <div style={{ display: "grid", gap: 6 }}>
            <label>Уровень доступа</label>
            <input
              type="text"
              value={
                userInfo?.access === 1
                  ? "Администратор"
                  : userInfo?.access === 0
                  ? "Пользователь"
                  : ""
              }
              readOnly
            />
          </div>
          <div style={{ display: "grid", gap: 6 }}>
            <label>Наименование организации</label>
            <input
              type="text"
              value={userInfo?.information?.name || ""}
              readOnly
            />
          </div>
          <div style={{ display: "grid", gap: 6 }}>
            <label>ИНН</label>
            <input
              type="text"
              value={userInfo?.information?.inn || ""}
              readOnly
            />
          </div>
          <div style={{ display: "grid", gap: 6 }}>
            <label>Комментарий администратора</label>
            <textarea
              value={userInfo?.adminComment || ""}
              readOnly
              style={{ height: 100 }}
            />
          </div>
        </div>
      </Popup>
    </Content>
  );
}
